import React, { useState, useEffect, useRef } from 'react';
import { Card, Input, Select, Textarea } from '@/components/ui';
import { platformSettingsService, PlatformSetting, SettingCategory, UpdateSettingRequest } from '@/services/platformSettingsService';
import { toast } from 'react-hot-toast';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import type { SelectOption } from '@/components/ui';

const SMTP_CREDENTIAL_KEYS = [
  'SMTP_HOST',
  'SMTP_PORT',
  'SMTP_USER',
  'SMTP_PASS',
  'SMTP_SECURE',
  'SMTP_FROM',
  'SMTP_FROM_NAME',
];
const SMTP_HEALTH_KEYS = ['SMTP_CONNECTION_STATUS', 'SMTP_LAST_ERROR'];

const PlatformSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<PlatformSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [selectedCategory, setSelectedCategory] = useState<SettingCategory | 'all'>('all');
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpDirty, setSmtpDirty] = useState(false);

  // Remove 'general' category, rename 'website' to 'Availability'
  const categories: SettingCategory[] = ['logger', 'smtp', 'website', 'system'];
  
  // Category display names
  const categoryDisplayNames: Record<string, string> = {
    'website': 'Availability',
    'logger': 'Logger',
    'smtp': 'Email',
    'system': 'System'
  };

  useEffect(() => {
    loadSettings();
  }, []); // Load once on mount, category filter is handled in render

  const loadSettings = async () => {
    try {
      setLoading(true);
      // Always load all settings to show platform information section
      const data = await platformSettingsService.getSettings();
      setSettings(data);
      
      // Initialize edited values
      const initialValues: Record<string, string> = {};
      data.forEach(setting => {
        if (setting.setting_key === 'SMTP_PASS') {
          // Never put masked password into the edit box — blank means "keep current"
          initialValues[setting.setting_key] = '';
        } else if (setting.setting_value !== null) {
          initialValues[setting.setting_key] = String(setting.setting_value);
        }
      });
      setEditedValues(initialValues);
      setSmtpDirty(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load platform settings');
      console.error('Error loading platform settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refs to store debounce timers for each setting
  const saveTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const handleValueChange = (key: string, value: string, setting: PlatformSetting) => {
    // Update the edited value immediately for UI responsiveness
    setEditedValues(prev => ({ ...prev, [key]: value }));

    // SMTP credentials are validated as a set — no per-field auto-save
    if (SMTP_CREDENTIAL_KEYS.includes(key)) {
      setSmtpDirty(true);
      return;
    }
    
    // Clear existing timer for this setting
    if (saveTimers.current[key]) {
      clearTimeout(saveTimers.current[key]);
      // Clear saving state if timer was cleared (user is still typing)
      setSaving(prev => ({ ...prev, [key]: false }));
    }
    
    // Debounce auto-save: wait 3 seconds of inactivity before saving
    saveTimers.current[key] = setTimeout(async () => {
      // Only now set saving state - when we actually start saving
      setSaving(prev => ({ ...prev, [key]: true }));
      
      try {
        const updates: UpdateSettingRequest = {
          setting_value: value || null
        };

        await platformSettingsService.updateSetting(setting.setting_key, updates);
        
        // Update the setting's value in the settings array to reflect the saved state
        setSettings(prev => prev.map(s => 
          s.setting_key === setting.setting_key 
            ? { ...s, setting_value: value || null }
            : s
        ));
        
        // Silent success - no toast for auto-save to avoid noise
      } catch (error: any) {
        toast.error(error.response?.data?.error || `Failed to update ${setting.setting_key}`);
        console.error('Error updating setting:', error);
        // Revert to original value on error
        setEditedValues(prev => ({
          ...prev,
          [key]: setting.setting_value !== null ? String(setting.setting_value) : ''
        }));
      } finally {
        setSaving(prev => ({ ...prev, [key]: false }));
        delete saveTimers.current[key];
      }
    }, 3000); // 3 seconds of inactivity before saving
  };

  const smtpStatus =
    (editedValues.SMTP_CONNECTION_STATUS ||
      settings.find((s) => s.setting_key === 'SMTP_CONNECTION_STATUS')?.setting_value ||
      'unknown') as string;
  const smtpLastError =
    editedValues.SMTP_LAST_ERROR ||
    settings.find((s) => s.setting_key === 'SMTP_LAST_ERROR')?.setting_value ||
    '';
  const smtpInvalid = smtpStatus === 'invalid';

  const saveSmtpSettings = async () => {
    setSmtpSaving(true);
    try {
      const passValue = editedValues.SMTP_PASS || '';
      const result = await platformSettingsService.updateSmtpSettings({
        SMTP_HOST: editedValues.SMTP_HOST || '',
        SMTP_PORT: editedValues.SMTP_PORT || '587',
        SMTP_USER: editedValues.SMTP_USER || '',
        SMTP_PASS: passValue === '***MASKED***' ? undefined : passValue,
        SMTP_SECURE: editedValues.SMTP_SECURE || 'false',
        SMTP_FROM: editedValues.SMTP_FROM || '',
        SMTP_FROM_NAME: editedValues.SMTP_FROM_NAME || '',
      });
      toast.success(result.message || 'SMTP settings verified and saved');
      setSmtpDirty(false);
      await loadSettings();
    } catch (error: any) {
      const message = error.response?.data?.error || 'SMTP validation failed — settings were not saved';
      toast.error(message);
      // Reflect invalid status from server without losing edited credential fields
      setEditedValues((prev) => ({
        ...prev,
        SMTP_CONNECTION_STATUS: 'invalid',
        SMTP_LAST_ERROR: message,
      }));
      setSettings((prev) =>
        prev.map((s) => {
          if (s.setting_key === 'SMTP_CONNECTION_STATUS') {
            return { ...s, setting_value: 'invalid' };
          }
          if (s.setting_key === 'SMTP_LAST_ERROR') {
            return { ...s, setting_value: message };
          }
          return s;
        })
      );
    } finally {
      setSmtpSaving(false);
    }
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(saveTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  const getInputType = (setting: PlatformSetting): string => {
    if (setting.is_sensitive) return 'password';
    if (setting.setting_type === 'number') return 'number';
    if (setting.setting_type === 'boolean') return 'checkbox';
    return 'text';
  };

  const getDropdownOptions = (settingKey: string): SelectOption[] => {
    switch (settingKey) {
      case 'WEBSITE_STATUS':
        return [
          { value: 'ACTIVE', label: 'Active' },
          { value: 'MAINTENANCE', label: 'Maintenance' },
          { value: 'DEPRECATED', label: 'Deprecated' }
        ];
      case 'LOGGER_LEVEL':
        return [
          { value: 'debug', label: 'Debug' },
          { value: 'info', label: 'Info' },
          { value: 'warn', label: 'Warning' },
          { value: 'error', label: 'Error' }
        ];
      default:
        return [];
    }
  };

  const renderInput = (setting: PlatformSetting) => {
    const currentValue = editedValues[setting.setting_key] || '';
    const isSaving = saving[setting.setting_key] || false;
    const isSmtpField = SMTP_CREDENTIAL_KEYS.includes(setting.setting_key);
    const invalidClass = isSmtpField && smtpInvalid
      ? 'border-[var(--color-error)] ring-1 ring-[var(--color-error)]/40'
      : '';

    // Check if this setting needs a dropdown
    const dropdownOptions = getDropdownOptions(setting.setting_key);
    if (dropdownOptions.length > 0) {
      return (
        <div className="relative">
          <Select
            value={currentValue}
            onChange={(e) => handleValueChange(setting.setting_key, e.target.value, setting)}
            options={dropdownOptions}
            placeholder={`Select ${setting.setting_key.replace(/_/g, ' ').toLowerCase()}`}
            disabled={setting.is_system && !setting.is_active || isSaving}
            className={`w-full ${invalidClass}`}
          />
          {isSaving && (
            <span className="absolute right-2 top-2 text-xs text-secondary-font">Saving...</span>
          )}
        </div>
      );
    }

    // Textarea for AVAILABILITY_MESSAGE
    if (setting.setting_key === 'AVAILABILITY_MESSAGE') {
      return (
        <div className="relative">
          <Textarea
            value={currentValue}
            onChange={(e) => handleValueChange(setting.setting_key, e.target.value, setting)}
            className="w-full"
            rows={3}
            placeholder={setting.description || `Enter ${setting.setting_key}`}
            disabled={setting.is_system && !setting.is_active || isSaving}
          />
          {isSaving && (
            <span className="absolute right-2 top-2 text-xs text-secondary-font">Saving...</span>
          )}
        </div>
      );
    }

    if (setting.setting_type === 'boolean') {
      return (
        <div className={`flex items-center gap-2 ${invalidClass ? 'p-2 rounded border border-[var(--color-error)]' : ''}`}>
          <input
            type="checkbox"
            checked={currentValue === 'true'}
            onChange={(e) => handleValueChange(setting.setting_key, e.target.checked ? 'true' : 'false', setting)}
            className="w-4 h-4"
            disabled={setting.is_system && !setting.is_active || isSaving || smtpSaving}
          />
          <span className="text-sm text-secondary-font">
            {currentValue === 'true' ? 'Enabled' : 'Disabled'}
          </span>
          {isSaving && (
            <span className="text-xs text-secondary-font ml-2">Saving...</span>
          )}
        </div>
      );
    }

    if (setting.setting_type === 'number') {
      return (
        <div className="relative">
          <Input
            type="number"
            value={currentValue}
            onChange={(e) => handleValueChange(setting.setting_key, e.target.value, setting)}
            className={`w-full ${invalidClass}`}
            disabled={setting.is_system && !setting.is_active || isSaving || smtpSaving}
          />
          {isSaving && (
            <span className="absolute right-2 top-2 text-xs text-secondary-font">Saving...</span>
          )}
        </div>
      );
    }

    return (
      <div className="relative">
        <Input
          type={getInputType(setting)}
          value={currentValue === '***MASKED***' && setting.setting_key === 'SMTP_PASS' ? '' : currentValue}
          onChange={(e) => handleValueChange(setting.setting_key, e.target.value, setting)}
          className={`w-full ${invalidClass}`}
          placeholder={
            setting.setting_key === 'SMTP_PASS'
              ? 'Enter new password to change (leave blank to keep current)'
              : setting.description || `Enter ${setting.setting_key}`
          }
          disabled={setting.is_system && !setting.is_active || isSaving || smtpSaving}
          autoComplete={setting.setting_key === 'SMTP_PASS' ? 'new-password' : undefined}
        />
        {isSaving && (
          <span className="absolute right-2 top-2 text-xs text-secondary-font">Saving...</span>
        )}
      </div>
    );
  };

  // Separate platform information fields (exclude WEBSITE_STATUS - it goes to Availability tab)
  const platformInfoKeys = ['PLATFORM_NAME', 'PLATFORM_VERSION', 'PLATFORM_DESCRIPTION', 'PLATFORM_CONTACT_EMAIL', 'PLATFORM_SUPPORT_URL', 'PLATFORM_LICENSE_INFO'];
  const platformInfoSettings = settings.filter(s => platformInfoKeys.includes(s.setting_key));
  
  // Availability settings (WEBSITE_STATUS and AVAILABILITY_MESSAGE)
  const availabilityKeys = ['WEBSITE_STATUS', 'AVAILABILITY_MESSAGE'];
  const availabilitySettings = settings.filter(s => availabilityKeys.includes(s.setting_key));
  
  // Other settings (exclude platform info, availability, and SMTP health system keys from editable groups)
  const otherSettings = settings.filter(
    (s) =>
      !platformInfoKeys.includes(s.setting_key) &&
      !availabilityKeys.includes(s.setting_key) &&
      !SMTP_HEALTH_KEYS.includes(s.setting_key)
  );

  // Filter settings by category
  const filteredSettings = selectedCategory === 'all' 
    ? otherSettings 
    : otherSettings.filter(s => s.category === selectedCategory);

  // Add availability settings to website category
  const finalFilteredSettings = selectedCategory === 'website' 
    ? [...filteredSettings, ...availabilitySettings]
    : filteredSettings;

  const groupedSettings = finalFilteredSettings.reduce((acc, setting) => {
    // For availability settings, group them under 'website' category
    const category = availabilityKeys.includes(setting.setting_key) ? 'website' : setting.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(setting);
    return acc;
  }, {} as Record<SettingCategory, PlatformSetting[]>);

  return (
    <div className="container mx-auto px-4 py-4">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Cog6ToothIcon className="w-6 h-6 text-primary-font" />
          <h1 className="text-2xl font-bold text-primary-font">
            Platform Settings
          </h1>
        </div>
        <p className="text-sm text-secondary-font">
          Manage platform-wide configuration settings
        </p>
      </div>

      {/* Category Filter */}
      <div className="mb-4 flex gap-2 flex-wrap">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-3 py-1.5 rounded text-sm font-medium ${
            selectedCategory === 'all'
              ? 'bg-[var(--color-primary-bg)] text-[var(--color-primary-font)]'
              : 'bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)]'
          }`}
        >
          All
        </button>
        {categories.map(category => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-3 py-1.5 rounded text-sm font-medium ${
              selectedCategory === category
                ? 'bg-[var(--color-primary-bg)] text-[var(--color-primary-font)]'
                : 'bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)]'
            }`}
          >
            {categoryDisplayNames[category] || category}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8">
          <p className="text-sm text-secondary-font">Loading settings...</p>
        </div>
      ) : (
        <>
          {/* Platform Information Section (only show on 'all' or 'system' tab) */}
          {platformInfoSettings.length > 0 && (selectedCategory === 'all' || selectedCategory === 'system') && (
            <Card className="p-4 mb-4">
              <h2 className="text-lg font-semibold text-primary-font mb-3">
                Platform Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {platformInfoSettings.map(setting => {
                  const isSaving = saving[setting.setting_key] || false;
                  const displayName = setting.setting_key.replace('PLATFORM_', '').replace(/_/g, ' ').toLowerCase()
                    .replace(/\b\w/g, l => l.toUpperCase());

                  return (
                    <div key={setting.id}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <label className="block text-sm font-medium text-primary-font">
                          {displayName}
                        </label>
                        {isSaving && (
                          <span className="text-xs text-secondary-font">Saving...</span>
                        )}
                      </div>
                      {setting.description && (
                        <p className="text-xs text-secondary-font mb-1">
                          {setting.description}
                        </p>
                      )}
                      {setting.setting_key === 'PLATFORM_DESCRIPTION' ? (
                        <div className="relative">
                          <Textarea
                            value={editedValues[setting.setting_key] || ''}
                            onChange={(e) => handleValueChange(setting.setting_key, e.target.value, setting)}
                            className="w-full"
                            rows={2}
                            placeholder={setting.description || `Enter ${displayName}`}
                            disabled={isSaving}
                          />
                          {isSaving && (
                            <span className="absolute right-2 top-2 text-xs text-secondary-font">Saving...</span>
                          )}
                        </div>
                      ) : (
                        renderInput(setting)
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Availability Section (only show on 'all' tab - on 'website' tab they appear in grouped settings) */}
          {availabilitySettings.length > 0 && selectedCategory === 'all' && (
            <Card className="p-4 mb-4">
              <h2 className="text-lg font-semibold text-primary-font mb-3">
                Availability
              </h2>
              
              <div className="space-y-3">
                {availabilitySettings.map(setting => {
                  const isSaving = saving[setting.setting_key] || false;
                  const displayName = setting.setting_key === 'WEBSITE_STATUS' 
                    ? 'Website Status' 
                    : setting.setting_key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());

                  return (
                    <div key={setting.id} className="border-b border-[var(--color-border)] pb-3 last:border-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <label className="block text-sm font-medium text-primary-font">
                              {displayName}
                            </label>
                            {setting.is_system && (
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">
                                System
                              </span>
                            )}
                            {isSaving && (
                              <span className="text-xs text-secondary-font">Saving...</span>
                            )}
                          </div>
                          {setting.description && (
                            <p className="text-xs text-secondary-font mb-1.5">
                              {setting.description}
                            </p>
                          )}
                          <div>
                            {renderInput(setting)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Other Settings by Category */}
          {finalFilteredSettings.length === 0 ? (
            <Card className="p-4">
              <p className="text-sm text-secondary-font text-center">No settings found for the selected category.</p>
            </Card>
          ) : (
            Object.entries(groupedSettings).map(([category, categorySettings]) => (
              <Card key={category} className="p-4 mb-4">
                <h2 className="text-lg font-semibold text-primary-font mb-2">
                  {categoryDisplayNames[category] || category} Settings
                </h2>
                {category === 'smtp' && (
                  <div className="mb-3 space-y-2">
                    <p className="text-sm text-secondary-font">
                      System email configuration (password resets, invites, verification codes). Changes are
                      tested against the SMTP server and only saved when the connection succeeds — then the
                      email service reloads immediately. Set SMTP_FROM to the address recipients see (required
                      when SMTP_USER is not itself an email, e.g. Amazon SES). SMTP_FROM_NAME is the friendly
                      name shown in the inbox (default: T-MOS product name).
                    </p>
                    {smtpInvalid && (
                      <div className="rounded-md border border-[var(--color-error)] bg-[var(--color-error)]/10 px-3 py-2 text-sm text-[var(--color-error)]">
                        <p className="font-medium">Email settings are invalid</p>
                        {smtpLastError && <p className="mt-1 break-words opacity-90">{smtpLastError}</p>}
                        <p className="mt-1">Correct the fields below and click Test &amp; Save.</p>
                      </div>
                    )}
                    {smtpStatus === 'valid' && !smtpDirty && (
                      <p className="text-sm text-[var(--color-success)]">
                        SMTP connection is valid. Outbound email is using these settings.
                      </p>
                    )}
                  </div>
                )}
                
                <div className="space-y-3">
                  {categorySettings.map(setting => {
                    const isSaving = saving[setting.setting_key] || false;

                    return (
                      <div key={setting.id} className="border-b border-[var(--color-border)] pb-3 last:border-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <label
                                className={`block text-sm font-medium ${
                                  category === 'smtp' && smtpInvalid
                                    ? 'text-[var(--color-error)]'
                                    : 'text-primary-font'
                                }`}
                              >
                                {setting.setting_key}
                              </label>
                              {setting.is_system && (
                                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded">
                                  System
                                </span>
                              )}
                              {setting.is_sensitive && (
                                <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-800 rounded">
                                  Sensitive
                                </span>
                              )}
                              {isSaving && (
                                <span className="text-xs text-secondary-font">Saving...</span>
                              )}
                            </div>
                            {setting.description && (
                              <p className="text-xs text-secondary-font mb-1.5">
                                {setting.description}
                              </p>
                            )}
                            <div>
                              {renderInput(setting)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {category === 'smtp' && (
                    <div className="pt-2 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => void saveSmtpSettings()}
                        disabled={smtpSaving}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] hover:opacity-90 disabled:opacity-50"
                      >
                        {smtpSaving ? 'Testing connection…' : 'Test & Save'}
                      </button>
                      {smtpDirty && !smtpSaving && (
                        <span className="text-xs text-secondary-font">Unsaved email changes</span>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default PlatformSettingsPage;
